/**
 * useChipLoginCall — single call surface that talks to fn_kiosk_chip_login
 * via whichever auth context is actually available on this page load.
 *
 * Priority order:
 *   1. **Kiosk JWT** in localStorage as `fw_kiosk_jwt`. This is what a paired
 *      production device carries. The call goes out via a dedicated supabase
 *      client whose Authorization header is the kiosk JWT, so PostgREST
 *      routes it as the `kiosk` role with the JWT's school_id claim.
 *   2. **Authenticated user session** managed by the regular supabase client.
 *      This is what an admin browser-tab developer hits. RTK Query handles it.
 *   3. **Neither** → throw so the caller can surface a "Bitte koppeln" message.
 *
 * The split exists because the production paired-kiosk path runs as a custom
 * PostgREST role (`kiosk`) without a Supabase auth user — the regular RTK
 * Query baseQuery wouldn't attach the right token.
 */
import { useCallback } from "react";

import { useKioskChipLoginMutation } from "../api/klassenbuchKioskApi";
import { getKioskSupabaseClient, getKioskToken } from "../../../app/kiosk/utils/kioskUtils";

import type { KioskLoginResponse } from "../types/chipTypes";

export function useChipLoginCall() {
  const [rtkLogin] = useKioskChipLoginMutation();

  return useCallback(
    async (uid: string): Promise<KioskLoginResponse> => {
      const kioskJwt = getKioskToken();

      if (kioskJwt) {
        // Paired-device path. Use the kiosk-JWT-headers client so PostgREST
        // sees `role: kiosk` + `school_id` claim.
        const client = getKioskSupabaseClient();
        if (!client) {
          throw new Error("Kiosk Supabase client could not be initialised");
        }
        const { data, error } = await client.rpc("fn_kiosk_chip_login", { p_uid: uid });
        if (error) throw error;
        return data as KioskLoginResponse;
      }

      // Dev path. Authenticated admin session → RTK Query.
      return await rtkLogin({ uid }).unwrap();
    },
    [rtkLogin],
  );
}
